// /opt/isp_erp/frontend/src/pages/admin/clientes/components/AsignarNapDialog.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Tooltip, Typography
} from '@mui/material';
import axios from '../../../../api/axios';

const distanciaTexto = (km) => {
  if (km === null || km === undefined) return '';
  return km < 1 ? `a ${Math.round(km * 1000)} m` : `a ${Number(km).toFixed(1)} km`;
};

/** Asigna o cambia la NAP y el puerto de un cliente. actual = { codigo, puerto } si ya tiene una. */
const AsignarNapDialog = ({ open, customerId, actual, onClose, onHecho }) => {
  const [texto, setTexto] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [cargandoNaps, setCargandoNaps] = useState(false);
  const [nap, setNap] = useState(null);
  const [puertos, setPuertos] = useState([]);
  const [cargandoPuertos, setCargandoPuertos] = useState(false);
  const [puerto, setPuerto] = useState(null);
  const [nivel, setNivel] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const contador = useRef(0);

  useEffect(() => {
    if (!open) return;
    setTexto(''); setNap(null); setPuertos([]); setPuerto(null); setNivel(''); setObservaciones(''); setError('');
  }, [open]);

  // Búsqueda de NAP en el servidor: espera 300 ms tras la última tecla
  useEffect(() => {
    if (!open || !customerId) return undefined;
    const id = ++contador.current;
    const espera = setTimeout(async () => {
      setCargandoNaps(true);
      try {
        const r = await axios.get(`/customers/${customerId}/naps-disponibles`, { params: texto.trim() ? { q: texto.trim() } : {} });
        if (id === contador.current) setOpciones(r.data.items || []);
      } catch (e) {
        if (id === contador.current) setOpciones([]);
      } finally {
        if (id === contador.current) setCargandoNaps(false);
      }
    }, 300);
    return () => clearTimeout(espera);
  }, [open, customerId, texto]);

  // Al elegir una NAP se traen sus puertos
  useEffect(() => {
    if (!nap) { setPuertos([]); setPuerto(null); return undefined; }
    let vigente = true;
    setCargandoPuertos(true);
    axios.get(`/customers/${customerId}/naps/${nap.id}/puertos`)
      .then((r) => { if (vigente) { setPuertos(r.data.puertos || []); setPuerto(null); } })
      .catch(() => { if (vigente) setPuertos([]); })
      .finally(() => { if (vigente) setCargandoPuertos(false); });
    return () => { vigente = false; };
  }, [nap, customerId]);

  const guardar = async () => {
    setError('');
    if (!nap) { setError('Elige una NAP'); return; }
    if (!puerto) { setError('Elige un puerto'); return; }
    let nivelNum = null;
    if (String(nivel).trim() !== '') {
      nivelNum = parseFloat(String(nivel).replace(',', '.'));
      if (Number.isNaN(nivelNum) || nivelNum > 0 || nivelNum < -60) {
        setError('El nivel de señal debe estar entre -60 y 0 dBm');
        return;
      }
    }
    setGuardando(true);
    try {
      await axios.put(`/customers/${customerId}/nap`, {
        nap_id: nap.id, numero_puerto: puerto, nivel_senal_rx: nivelNum, observaciones: observaciones.trim() || null
      });
      onHecho();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo asignar la NAP');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{actual ? 'Cambiar NAP' : 'Asignar NAP'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {actual && (
            <Alert severity="info">
              Conexión actual: NAP <strong>{actual.codigo}</strong>, puerto <strong>{actual.puerto}</strong>. Al guardar, ese puerto quedará libre.
            </Alert>
          )}
          <Autocomplete
            size="small"
            options={opciones}
            value={nap}
            loading={cargandoNaps}
            filterOptions={(o) => o}
            onChange={(_, v) => setNap(v)}
            onInputChange={(_, v, motivo) => {
              if (motivo === 'input') setTexto(v);
              if (motivo === 'clear') setTexto('');
            }}
            getOptionLabel={(o) => (o ? `${o.codigo} — ${o.nombre}` : '')}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionDisabled={(o) => !o.disponible}
            noOptionsText={cargandoNaps ? 'Buscando...' : 'No hay NAP que coincidan'}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {o.codigo} — {o.nombre} {o.es_actual && <Chip size="small" label="actual" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {o.disponible
                      ? `${o.puertos_libres} de ${o.capacidad_puertos} puertos libres ${distanciaTexto(o.distancia_km)}${o.aviso ? ` · ${o.aviso}` : ''}`
                      : `${o.motivo}`}
                    {o.sector || o.zona ? ` · ${o.sector || o.zona}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="NAP (escribe código, nombre, zona o sector; se ordenan por cercanía)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{cargandoNaps ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                }}
              />
            )}
          />

          {nap && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Puerto</Typography>
              {cargandoPuertos ? <CircularProgress size={20} /> : (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {puertos.map((p) => {
                    const chip = (
                      <Chip
                        key={p.numero}
                        label={p.numero}
                        color={p.es_del_cliente ? 'primary' : (p.disponible ? 'success' : 'default')}
                        variant={puerto === p.numero ? 'filled' : 'outlined'}
                        disabled={!p.disponible}
                        onClick={p.disponible ? () => setPuerto(p.numero) : undefined}
                        sx={{ minWidth: 44, fontWeight: puerto === p.numero ? 'bold' : 'normal' }}
                      />
                    );
                    if (p.es_del_cliente) return <Tooltip key={p.numero} title="Puerto actual de este cliente">{chip}</Tooltip>;
                    if (!p.disponible) {
                      const motivo = p.ocupado_por ? `Ocupado por ${p.ocupado_por}` : `Puerto ${String(p.estado).toLowerCase()}`;
                      return <Tooltip key={p.numero} title={motivo}><span>{chip}</span></Tooltip>;
                    }
                    return chip;
                  })}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary">Verde: libre · Azul: puerto actual del cliente · Gris: no disponible</Typography>
            </Box>
          )}

          <TextField size="small" label="Nivel de señal RX en dBm (opcional)" value={nivel} onChange={(e) => setNivel(e.target.value)} placeholder="-22.5" />
          <TextField size="small" label="Observaciones (opcional)" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} multiline minRows={2} inputProps={{ maxLength: 300 }} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={guardando || !nap || !puerto}>
          {guardando ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AsignarNapDialog;
